/**
 * Tab Publicación - TICKET JXWPR9
 * 
 * Tab para publicar curso con validación.
 * Muestra errores de validación por actividad y permite publicar.
 */

import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader, XCircle } from 'lucide-react';
import apiClient from '../../services/apiClient';

const TabPublicacion = ({ course, onCourseUpdate }) => {
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const handlePublish = async () => {
    if (!window.confirm('¿Estás seguro de publicar este curso? Una vez publicado, estará visible para los estudiantes.')) {
      return;
    }

    setPublishing(true);
    setPublishError(null);
    setPublishSuccess(false);
    setValidationErrors([]);

    try {
      const response = await apiClient.post(`/admin/courses/${course.courseId}/publish`);
      
      setPublishSuccess(true);
      onCourseUpdate(response.data.course);

      // Auto-hide success después de 5s
      setTimeout(() => {
        setPublishSuccess(false);
      }, 5000);

    } catch (err) {
      console.error('Error publicando curso:', err);
      
      if (err.response?.status === 400 && err.response?.data?.details) {
        // Errores de validación
        setValidationErrors(err.response.data.details);
        setPublishError('El curso tiene errores de validación. Corrígelos antes de publicar.');
      } else {
        setPublishError(err.response?.data?.message || err.message || 'Error al publicar curso');
      }
    } finally {
      setPublishing(false);
    }
  };

  const isPending = course.status === 'draft';
  const isPublished = course.status === 'published';
  const isArchived = course.status === 'archived';

  return (
    <div className="space-y-6">
      {/* Estado actual */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado actual del curso</h3>
        
        <div className="flex items-center gap-3">
          {isPending && (
            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-100 text-yellow-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Borrador
            </span>
          )}
          {isPublished && (
            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-green-100 text-green-700 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Publicado
            </span>
          )}
          {isArchived && (
            <span className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700 flex items-center gap-2">
              <XCircle className="w-4 h-4" />
              Archivado
            </span>
          )}
        </div>

        {isPublished && (
          <p className="text-sm text-gray-600 mt-3">
            Este curso está activo y visible para los estudiantes en el catálogo público.
          </p>
        )}
        {isPending && (
          <p className="text-sm text-gray-600 mt-3">
            El curso no es visible para los estudiantes. Completa todos los campos obligatorios y publícalo.
          </p>
        )}
      </div>

      {/* Success message */}
      {publishSuccess && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-900">Curso publicado exitosamente</p>
            <p className="text-sm text-green-700 mt-1">
              El curso ya está disponible en el catálogo público para los estudiantes.
            </p>
          </div>
        </div>
      )}

      {/* Error message (no validation) */}
      {publishError && validationErrors.length === 0 && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-900">Error al publicar</p>
            <p className="text-sm text-red-700 mt-1">{publishError}</p>
          </div>
        </div>
      )}

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 rounded-lg border border-red-200 p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900">Errores de validación</h4>
              <p className="text-sm text-red-700 mt-1">
                El curso tiene {validationErrors.length} error{validationErrors.length > 1 ? 'es' : ''} que deben corregirse antes de publicar.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {validationErrors.map((error, index) => (
              <div key={index} className="bg-white rounded-lg p-4 border border-red-200">
                <div className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{error.message}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-gray-600">
                      {error.moduleId && (
                        <>
                          <span className="font-mono">Módulo: {error.moduleId}</span>
                          {error.activityId && <span>→</span>}
                        </>
                      )}
                      {error.activityId && (
                        <span className="font-mono">Actividad: {error.activityId}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Cómo corregir:</strong> Ve al Tab Temario, busca las actividades mencionadas y completa los campos faltantes (URLs, agentes VRT, labs EPL).
            </p>
          </div>
        </div>
      )}

      {/* Checklist de validación */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Checklist antes de publicar</h3>
        
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Información general completa</p>
              <p className="text-sm text-gray-600">Título, descripción, horas estimadas, skills</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Al menos 1 módulo con 1 actividad</p>
              <p className="text-sm text-gray-600">El curso debe tener contenido educativo</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Actividades Resource configuradas</p>
              <p className="text-sm text-gray-600">URLs de YouTube/Links/Textos completos</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">4</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Simulaciones VRT asignadas</p>
              <p className="text-sm text-gray-600">Cada actividad VRT debe tener un agente seleccionado</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-blue-700 text-xs font-bold">5</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Labs EPL asignados</p>
              <p className="text-sm text-gray-600">Cada actividad EPL debe tener un lab seleccionado</p>
            </div>
          </div>
        </div>
      </div>

      {/* Botón publicar */}
      {isPending && (
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">¿Listo para publicar?</h3>
          <p className="text-sm text-gray-600 mb-4">
            Una vez publicado, el curso estará visible en el catálogo público. Los estudiantes podrán inscribirse y comenzar a aprender.
          </p>
          
          <button
            onClick={handlePublish}
            disabled={publishing}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium"
          >
            {publishing ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                Publicando curso...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Publicar curso
              </>
            )}
          </button>
        </div>
      )}

      {isPublished && (
        <div className="bg-green-50 rounded-lg border border-green-200 p-6">
          <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <h3 className="text-center text-lg font-semibold text-gray-900 mb-2">Curso publicado</h3>
          <p className="text-center text-sm text-gray-600">
            Este curso ya está disponible en el catálogo. Puedes editarlo en cualquier momento.
          </p>
        </div>
      )}
    </div>
  );
};

export default TabPublicacion;
